import { fromServer, t } from './i18n';
import { getToken } from './storage';

// Same Laravel backend as the website (routes/api.php, prefix /ops).
export const API_URL = process.env.EXPO_PUBLIC_API_URL ?? 'https://test.dawaaalhayat.com/api';

export class ApiError extends Error {
  constructor(message: string, public code: number) {
    super(message);
  }
}

let onUnauthorized: (() => void) | null = null;
/** Called when the server says the session is over (401) or the account was switched off (403 on /ops). */
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

/**
 * The backend answers HTTP 200 with {code, data} or {code, error: {message}}.
 * Returns `data` (or the plain message) and throws ApiError with the message in the app's language.
 */
export async function api<T = any>(path: string, options: { method?: string; body?: unknown; token?: string | null } = {}): Promise<T> {
  const token = options.token !== undefined ? options.token : await getToken();
  let res: Response;
  try {
    res = await fetch(API_URL + path, {
      method: options.method ?? (options.body ? 'POST' : 'GET'),
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        // The ops API always answers in Arabic; the app translates (i18n.fromServer).
        locale: 'ar',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
  } catch {
    throw new ApiError(t('لا يوجد اتصال بالإنترنت', 'No internet connection'), 0);
  }

  const serverError = t('حدث خطأ في الخادم، حاول مرة أخرى', 'Server error, please try again');
  let json: any = null;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(serverError, res.status);
  }
  const code = Number(json?.code ?? res.status);
  if (code === 200) {
    return (json.data ?? json.message) as T;
  }
  const message = fromServer(json?.error?.message || json?.message) || t('حدث خطأ، حاول مرة أخرى', 'Something went wrong, please try again');
  if (code === 401 || (code === 403 && path.startsWith('/ops/') && path !== '/ops/login')) {
    if (path !== '/ops/login') onUnauthorized?.();
  }
  // A 500 keeps the server's reason when it wrote one on purpose (Arabic, e.g. "تعذّر … السبب: …").
  const deliberate = /[؀-ۿ]/.test(String(json?.error?.message ?? ''));
  throw new ApiError(code === 500 && !deliberate ? serverError : message, code);
}

export const money = (n: number | null | undefined) =>
  n === null || n === undefined ? '—' : `${Math.round(Number(n)).toLocaleString('en-US')} ${t('د.ع', 'IQD')}`;

export const hoursText = (h: number | null | undefined) => {
  if (h === null || h === undefined) return '—';
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} ${t('دقيقة', 'min')}`;
  if (h < 48) return `${Number(h.toFixed(1))} ${t('ساعة', 'h')}`;
  return `${Math.round(h / 24)} ${t('يوم', 'days')}`;
};

export const dateTime = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())}  ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
